import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import * as actions from '../../../../store';
import { useAppliedEvaluation, useEvaluation } from '../../../../helpers/hooks/useEvaluation';
import { getCurrentModuleStatus } from '../Summary/SummaryCommon/SummaryCommon';
import { Button, Tooltip, Typography, Fab, Divider, useMediaQuery } from '@material-ui/core';
import {
  handleRevisionConfirm,
  handleRevisionExit,
  handleRevisionDiscard,
  handleRevisionSave,
} from '../../../../store/slices/labeling';
import { ExitToApp } from '@material-ui/icons';
import ReplayIcon from '@material-ui/icons/Replay';
import SaveIcon from '@material-ui/icons/Save';

const useStyles = makeStyles((theme) => ({
  button_group_container: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: '4px',
  },

  revise_card_fabs: {
    margin: '6px 10px',
    height: '22px',
    width: '22px',
    minHeight: '22px',
    minWidth: '22px',
    backgroundColor: theme.palette.accents.dark,

    '&:hover': {
      backgroundColor: theme.palette.accents.main,
      boxShadow: '0px 0px 5px 0px #d7c2c2',
    },
  },

  save_action_fabs: {
    margin: '6px 10px',
    height: '22px',
    minHeight: '22px',
    minWidth: '100px',
    backgroundColor: theme.palette.accents.dark,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.accents.main,
      boxShadow: '0px 0px 5px 0px #d7c2c2',
    },
  },

  discard_action_fabs: {
    margin: '6px 10px',
    height: '22px',
    width: '22px',
    minHeight: '22px',
    minWidth: '22px',
    backgroundColor: 'rgb(109, 109, 109)',
    '&:hover': {
      backgroundColor: theme.palette.accents.dark,
      boxShadow: '0px 0px 5px 0px #d7c2c2',
    },
  },

  tooltipText: {
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    fontSize: theme.fonts.responsiveMedium,
  },
}));

const ReviseCardActions = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();

  // Store state
  const { examinationListDrawer } = useSelector((state) => state.gui);
  const { premium_features } = useSelector((store) => store.global_settings || {});

  const {
    densityInRevisionMode,
    diagnosticsInRevisionMode,
    qualityInDrawingMode,
    localChangesMicrocalc,
    localChangesOpacities,
    localChangesQuality,
    localChangesDensity,
    qualityInRevisionMode,
  } = useSelector((state) => state.labeling);
  const results = useSelector((state) => state.results);
  const { data_annotation } = premium_features;
  const {
    applied_density,
    applied_quality,
    confirmed_quality,
    confirmed_density,
    applied_microcalc,
    applied_opacities,
    confirmed_microcalc,
    confirmed_opacities,
    shown_density,
    shown_microcalc,
    shown_opacities,
    shown_quality,
  } = results;

  const smallScreen = useMediaQuery('(max-width: 1400px)') || examinationListDrawer;

  const densityShown = shown_density || applied_density;
  const qualityShown = shown_quality || applied_quality;
  const microcalcShown = shown_microcalc || applied_microcalc;
  const opacitiesShown = shown_opacities || applied_opacities;
  const resultMicrocalcEvaluation = results?.evaluations?.microcalc?.[microcalcShown];
  const resultOpacitiesEvaluation = results?.evaluations?.opacities?.[opacitiesShown];
  const resultDensityEvaluation = results?.evaluations?.density?.[densityShown];
  const resultQualityEvaluation = results?.evaluations?.quality?.[qualityShown];

  const moduleConfirmed = qualityInRevisionMode
    ? applied_quality === 'bbox' && confirmed_quality
    : applied_density === 'bbox' && confirmed_density;
  const moduleRelabeled = qualityInRevisionMode
    ? confirmed_quality && applied_quality !== 'bbox'
    : confirmed_density && applied_density !== 'bbox';
  const appliedresult = qualityInRevisionMode ? applied_quality : applied_density;
  const annotatorRole = data_annotation && localStorage.getItem('annotation') === 'true';

  const someLocalChanges =
    (qualityInRevisionMode && localChangesQuality) ||
    (densityInRevisionMode && localChangesDensity) ||
    (diagnosticsInRevisionMode && (localChangesMicrocalc || localChangesOpacities));

  // Helper function to get module status information for Quality/Density
  const getModuleStatusInfo = (moduleOpen) => {
    const moduleType = qualityInRevisionMode
      ? t('ResultView.quality')
      : densityInRevisionMode
      ? t('ResultView.density')
      : t('ResultView.diagnostics');

    const shownResult = moduleOpen === 'quality' ? qualityShown : densityShown;

    return getCurrentModuleStatus(
      appliedresult,
      shownResult,
      moduleConfirmed,
      moduleRelabeled,
      moduleType,
      theme,
      t
    );
  };

  // Helper function to get microcalc module status information
  const getMicrocalcModuleStatusInfo = () => {
    return getCurrentModuleStatus(
      applied_microcalc || 'bbox',
      shown_microcalc || 'bbox',
      applied_microcalc === 'bbox' && confirmed_microcalc,
      confirmed_microcalc && applied_microcalc !== 'bbox',
      t('ResultView.diagnose_microcalc'),
      theme,
      t
    );
  };

  // Helper function to get opacities module status information
  const getOpacitiesModuleStatusInfo = () => {
    return getCurrentModuleStatus(
      applied_opacities || 'bbox',
      shown_opacities || 'bbox',
      applied_opacities === 'bbox' && confirmed_opacities,
      confirmed_opacities && applied_opacities !== 'bbox',
      t('ResultView.diagnose_opacities'),
      theme,
      t
    );
  };

  const getDiagnosticsModuleStatusInfo = () => {
    const microcalcStatus = getMicrocalcModuleStatusInfo();
    const opacitiesStatus = getOpacitiesModuleStatusInfo();

    return {
      buttonText:
        opacitiesStatus.buttonText === microcalcStatus.buttonText
          ? opacitiesStatus.buttonText
          : t('ResultView.confirm'),
      currentApplied:
        opacitiesStatus.currentApplied ||
        microcalcStatus.currentApplied ||
        `${t('ResultView.diagnostics')}: ${shown_microcalc || shown_opacities || 'bbox'}`,
      tooltipAction:
        opacitiesStatus.tooltipAction ||
        microcalcStatus.tooltipAction ||
        t('ResultView.btn_confirm', { module: t('ResultView.diagnostics') }),
      fabIcon: opacitiesStatus.fabIcon || microcalcStatus.fabIcon,
      color: '#515151',
    };
  };

  // Helper function to get the appropriate module status based on current mode
  const getCurrentModuleStatusInfo = () => {
    if (qualityInRevisionMode) {
      return getModuleStatusInfo('quality');
    } else if (densityInRevisionMode) {
      return getModuleStatusInfo('density');
    } else if (diagnosticsInRevisionMode) {
      return getDiagnosticsModuleStatusInfo();
    }
    return null;
  };

  const onConfirmClick = () => {
    dispatch(handleRevisionConfirm(t));
  };

  const exitRevisionMode = () => {
    dispatch(handleRevisionExit());
  };

  const discardCurrentChanges = () => {
    dispatch(handleRevisionDiscard(true));
  };

  const saveResults = () => {
    dispatch(handleRevisionSave(t));
  };

  // Render confirm / discard / save button
  const renderConfirmButton = () => {
    if (!annotatorRole || !(qualityInRevisionMode || densityInRevisionMode || diagnosticsInRevisionMode)) {
      return null;
    }

    const moduleStatus = getCurrentModuleStatusInfo();
    if (!moduleStatus) return null;

    return (
      <Tooltip
        arrow
        placement='top'
        title={
          <>
            <Typography className={classes.tooltipText} style={{ fontWeight: 'bold' }}>
              {moduleStatus.currentApplied}
            </Typography>
            <Divider style={{ margin: '3% 0' }} />
            <Typography className={classes.tooltipText}>{moduleStatus.tooltipAction}</Typography>
          </>
        }>
        {smallScreen ? (
          <Fab
            color='primary'
            className={classes.revise_card_fabs}
            onClick={() => {
              onConfirmClick();
            }}>
            {moduleStatus.fabIcon}
          </Fab>
        ) : (
          <Button
            color='primary'
            // startIcon={moduleStatus.fabIcon}
            style={{
              color: '#fff',
              // background: moduleStatus.color,
              margin: '4px',
              textWrap: 'nowrap',
            }}
            className={classes.save_action_fabs}
            onClick={() => {
              onConfirmClick();
            }}>
            {moduleStatus.buttonText}
          </Button>
        )}
      </Tooltip>
    );
  };

  return (
    <div className={classes.button_group_container}>
      <>
        <Tooltip
          title={<Typography className={classes.tooltipText}>{t('ResultView.btn_exit_revision')}</Typography>}
          arrow
          placement='top-start'>
          <>
            {smallScreen ? (
              <Fab
                size='small'
                onClick={() => exitRevisionMode()}
                color='primary'
                className={classes.revise_card_fabs}>
                <ExitToApp
                  fontSize='small'
                  style={{ width: '18px', height: '18px', transform: 'scaleX(-1)' }}
                />
              </Fab>
            ) : (
              <Button
                variant='contained'
                size='small'
                id='exit_revision_mode'
                className={classes.save_action_fabs}
                onClick={() => exitRevisionMode()}>
                <ExitToApp
                  fontSize='small'
                  style={{ width: '18px', height: '18px', transform: 'scaleX(-1)', marginRight: '4px' }}
                />
                {t('ResultView.btn_exit')}
              </Button>
            )}
          </>
        </Tooltip>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '100%',
          }}>
          <>
            {someLocalChanges ? (
              <>
                {!qualityInDrawingMode && (
                  <Tooltip
                    title={
                      <Typography className={classes.tooltipText}>{t('ResultView.btn_discard')}</Typography>
                    }
                    arrow
                    placement='top-start'>
                    <Fab
                      size='small'
                      className={classes.discard_action_fabs}
                      color='primary'
                      onClick={() => {
                        discardCurrentChanges();
                      }}>
                      <ReplayIcon fontSize='small' style={{ width: '18px', height: '18px' }} />
                    </Fab>
                  </Tooltip>
                )}
                <Tooltip
                  title={<Typography className={classes.tooltipText}>{t('ResultView.btn_save')}</Typography>}
                  arrow
                  placement='top-start'>
                  <Button
                    size='small'
                    onClick={() => saveResults()}
                    className={classes.save_action_fabs}
                    startIcon={<SaveIcon fontSize='small' style={{ width: '18px', height: '18px' }} />}>
                    {t('ResultView.save')}
                  </Button>
                </Tooltip>
              </>
            ) : (
              renderConfirmButton()
            )}
          </>
        </div>
      </>
    </div>
  );
};
export default ReviseCardActions;
